import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  useMediaQuery,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import { Onfido } from "onfido-sdk-ui";

export const CONTAINER_ID = "onfido-mount";

export interface IEntrustSdkProps {
  open: boolean;
  sdkToken: string;
  workflowRunId: string;
  loading?: boolean;
  onComplete: () => void;
  onClose: () => void;
  onError?: (error: Error) => void;
}

const useStyles = makeStyles((theme) => ({
  paper: {
    width: "min(90vw, 900px)",
    height: "min(92vh, 900px)",
    margin: theme.spacing(3),
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    position: "relative",

    [theme.breakpoints.down("sm")]: {
      width: "100vw",
      height: "100vh",
      margin: 0,
      borderRadius: 0,
    },
  },

  title: {
    flexShrink: 0,
    paddingRight: theme.spacing(6),
  },

  content: {
    position: "relative",
    flex: 1,
    padding: 0,
    overflow: "hidden",
  },

  sdkContainer: {
    width: "100%",
    height: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },

  loader: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: 1,
  },

  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    zIndex: 10,
  },
}));

export const EntrustSdk: React.FC<IEntrustSdkProps> = ({
  open,
  sdkToken,
  workflowRunId,
  loading,
  onComplete,
  onClose,
  onError,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const sdkRef = useRef<any>(null);
  const [sdkLoading, setSdkLoading] = useState(true);

  useEffect(() => {
    if (!open || !sdkToken || !workflowRunId) {
      return;
    }
     if (sdkRef.current) return;

    setSdkLoading(true);

    const sdk = Onfido.init({
      token: sdkToken,
      workflowRunId,
      containerId: CONTAINER_ID,

      onComplete: () => {
        onComplete();
      },

      onError: ({ type, message }) => {
        console.error("Entrust SDK error", {
          type,
          message,
        });

        if (onError) {
          onError(new Error(message));
        }
      },
    });

    sdkRef.current = sdk;

    // SDK has been mounted.
    requestAnimationFrame(() => {
      setSdkLoading(false);
    });

    return () => {
      if (sdkRef.current) {
        sdkRef.current.tearDown();
        sdkRef.current = null;
      }
    };
  }, [
    open,
    sdkToken,
    workflowRunId,
    onComplete,
    onError,
  ]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth={false}
      PaperProps={{
        className: classes.paper,
      }}
    >
      <DialogTitle className={classes.title}>
        Identity verification

        <IconButton
          className={classes.closeButton}
          onClick={onClose}
          size="small"
          aria-label="Close verification"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.content}>
        {/* {(loading || sdkLoading) && (
          <CircularProgress className={classes.loader} />
        )} */}

        <Box
          id={CONTAINER_ID}
          className={classes.sdkContainer}
        />
      </DialogContent>
    </Dialog>
  );
};
